import React, { ChangeEvent } from 'react';
import { InputAdornment, styled, TextField, TextFieldProps, useTheme } from '@mui/material';
import { Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Yup from '../../../../lib/validator';

const CustomTextField = styled(TextField)(() => ({
  '& .MuiInputLabel-root': {
    fontSize: '1em',
  },
  '& .MuiInputBase-input': {
    fontSize: '0.9em',
    paddingBottom: '3px',
  },
  '& .MuiInputBase-root': {
    height: '38px',
    paddingBottom: '3px',
  },
}));

const numberFormat = (value: string | number): string | number => {
  return String(value || '')
    ?.split('')
    ?.reverse()
    ?.reduce((accumulator, currentValue, currentIndex) => {
      if (currentIndex % 3 === 0 && currentIndex !== 0) return `${accumulator}.${currentValue}`;
      return `${accumulator}${currentValue}`;
    }, '')
    ?.split('')
    ?.reverse()
    .join('');
};
type TextAlign = 'left' | 'right';
export const InputMoneyAdapter = (
  props: TextFieldProps & {
    name: string;
    control: Control;
    schema: Yup.AnySchema;
    error?: string;
    onBlur?: any;
    customOnChange?: any;
    length?: number;
    textAlign?: TextAlign;
    type?: string;
    withCurrency?: boolean;
    staticCurrency?: boolean;
  }
) => {
  const {
    name,
    control,
    schema,
    onBlur,
    customOnChange,
    type = 'number',
    textAlign,
    length = 50,
    withCurrency = false,
    staticCurrency = false,
    ...rest
  } = props;

  const {
    field: { onChange, value, ref },
    fieldState: { invalid, error: err },
  } = useController({
    name,
    control,
    rules: {
      validate: (val) => schema.validate(val).catch((error) => error.message),
    },
    defaultValue: '',
  });

  const { t } = useTranslation();
  const theme = useTheme();

  const formatValues = (valueToFormat: string) => {
    if (type === 'number') {
      if (valueToFormat === '') return null;
      if (/^[0-9]+$/.test(`${valueToFormat}`)) {
        if (/^00.*|^0[\d\w]+/.test(`${valueToFormat}`))
          return `${valueToFormat?.replace(/\./g, '')}`;
        return `${valueToFormat}`;
      }
      return value as string;
    }
    if (valueToFormat === '') return null;

    return `${valueToFormat}`;
  };

  const dispatchOnchange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    onChange(e);
    if (customOnChange) customOnChange(e);
  };
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    const customEvent = {
      ...e,
      target: {
        ...e?.target,
        value: formatValues(rawValue),
      },
    } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
    if (e?.target?.value?.toString()?.length <= length) {
      dispatchOnchange(customEvent);
    }
  };

  return (
    <CustomTextField
      {...rest}
      name={name}
      ref={ref}
      value={(() => {
        if (!value) return '';
        const formattedValue = numberFormat(value);
        return withCurrency ? `$ ${formattedValue}` : formattedValue;
      })()}
      size="small"
      fullWidth
      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
        handleChange({
          ...e,
          target: {
            ...e?.target,
            name,
            value: String(e?.target?.value)?.split('.').join('')?.toString(),
          },
        })
      }
      onBlur={onBlur}
      error={rest?.disabled ? false : invalid}
      helperText={rest?.disabled ? '' : t(err?.message || '')}
      sx={{ mb: theme.spacing(1), width: '100%' }}
      InputProps={{
        inputProps: {
          style: { textAlign: textAlign || 'left' },
          ...rest.inputProps,
        },
        startAdornment: staticCurrency ? (
          <InputAdornment position="start">$</InputAdornment>
        ) : undefined,
      }}
    />
  );
};
